// In-app Toast + ConfirmDialog, replacing native alert()/confirm().
//
//   const { toast, confirm } = useUI();
//   toast('Сохранено');                         // info pill, auto-dismiss
//   toast('Не удалось', { type: 'error' });      // info | success | error | warn
//   if (!(await confirm('Удалить?'))) return;    // returns Promise<boolean>
//   await confirm('Удалить навсегда?', { danger: true, confirmText: 'Удалить' });
//
// Rendered once at app root (see main.jsx). Children are passed through as a
// stable prop, so toast/dialog state changes here do not re-render the app tree.
import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useSettings } from '../context/SettingsContext.jsx';

const UIContext = createContext(null);

// Fallback keeps call sites working even if the provider is missing.
export const useUI = () => useContext(UIContext) || {
  toast: () => {},
  confirm: (m) => Promise.resolve(window.confirm(m)),
};

const BAR = { info: '#e85d04', success: '#16894f', error: '#ef4444', warn: '#f59e0b' };

export function ToastProvider({ children }) {
  const { t } = useSettings();
  const [toasts, setToasts] = useState([]);       // {id, message, type}
  const [dialog, setDialog] = useState(null);     // {message, opts, resolve}
  const seq = useRef(0);

  const dismiss = useCallback((id) => setToasts((ts) => ts.filter((x) => x.id !== id)), []);
  const toast = useCallback((message, opts = {}) => {
    const id = ++seq.current;
    setToasts((ts) => [...ts, { id, message: String(message ?? ''), type: opts.type || 'info' }]);
    const dur = opts.duration ?? 3200;
    if (dur > 0) setTimeout(() => dismiss(id), dur);
    return id;
  }, [dismiss]);

  const confirm = useCallback((message, opts = {}) =>
    new Promise((resolve) => setDialog({ message: String(message ?? ''), opts, resolve })), []);
  const closeDialog = useCallback((val) => {
    setDialog((d) => { if (d) d.resolve(val); return null; });
  }, []);

  useEffect(() => {
    if (!dialog) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeDialog(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, closeDialog]);

  const tx = (key, fallback) => (t && t(key) !== key ? t(key) : fallback);

  return (
    <UIContext.Provider value={{ toast, confirm }}>
      {children}

      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 80, display: 'flex',
                    flexDirection: 'column', alignItems: 'center', gap: 8, zIndex: 9999, pointerEvents: 'none' }}>
        {toasts.map((tt) => (
          <div key={tt.id} role="status" onClick={() => dismiss(tt.id)}
            style={{ pointerEvents: 'auto', cursor: 'pointer', maxWidth: 'min(90%, 460px)',
                     padding: '11px 16px', borderRadius: 10, background: 'rgba(20,24,31,0.97)',
                     color: '#ece6d6', fontSize: 13.5, lineHeight: 1.4,
                     boxShadow: '0 6px 24px rgba(0,0,0,0.4)', borderLeft: `3px solid ${BAR[tt.type] || BAR.info}` }}>
            {tt.message}
          </div>
        ))}
      </div>

      {dialog && (
        <div onClick={() => closeDialog(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex',
                   alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
            style={{ width: '100%', maxWidth: 360, background: 'var(--app-card,#121216)',
                     border: '1px solid #ffffff14', borderRadius: 14, padding: 22,
                     boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
            {dialog.opts.title && (
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--app-text,#ece6d6)', marginBottom: 8 }}>
                {dialog.opts.title}
              </div>
            )}
            <div style={{ fontSize: 13.5, color: 'var(--app-text,#d8d2c8)', lineHeight: 1.55, marginBottom: 20 }}>
              {dialog.message}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => closeDialog(false)}
                style={{ padding: '9px 16px', borderRadius: 9, background: 'transparent',
                         border: '1px solid #ffffff2e', color: 'var(--app-text,#ece6d6)',
                         cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                {dialog.opts.cancelText || tx('ui_cancel', 'Отмена')}
              </button>
              <button onClick={() => closeDialog(true)}
                style={{ padding: '9px 16px', borderRadius: 9,
                         background: dialog.opts.danger ? '#b3261e' : 'var(--app-accent,#c9a24b)',
                         border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                {dialog.opts.confirmText || tx('ui_confirm', 'ОК')}
              </button>
            </div>
          </div>
        </div>
      )}
    </UIContext.Provider>
  );
}

export default ToastProvider;
